import { useState } from 'react'
import { Repeat2 } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import {
  shareToX,
  shareToFacebook,
  shareToLine,
  shareToWhatsApp,
  shareToInstagramPost,
  shareRepostOnX,
  shareImageViaWebShare,
  buildShareUrl,
  combineHashtags,
  extractTweetId,
  isInstagramPostUrl,
  resolveOfficialPostUrls,
  type ShareTarget,
  type ShareResult,
} from '../utils/share'
import { api } from '../services/api'
import { showToast } from './Toast'
import InstagramStoryHintModal from './InstagramStoryHintModal'
import type { Store } from '../store/useStore'

type ShareSource = 'review' | 'payment_complete' | 'store_detail'

interface Props {
  storeId: string
  storeName: string
  /** The store's `snsMedia` map as-is. Officially curated post URLs /
   *  caption / custom hashtags / curated image are all resolved here so
   *  the three call sites (Review / PaymentComplete / ShareSheet) stay
   *  a one-prop pass-through. */
  snsMedia?: Store['snsMedia']
  source: ShareSource
  /** Optional callback fired after a share button is tapped (e.g. close a parent modal). */
  onShared?: (result: ShareResult) => void
}

/**
 * Two-tier share UI (#69) shared by Review, PaymentComplete, and the
 * StoreDetail bottom sheet.
 *
 * Tier 1 (only when the store curated an official post): amplify the
 * store's own post — one-tap X repost / open the IG post and reshare it
 * to the customer's story. Concentrates engagement on a single post.
 *
 * Tier 2 (always): the customer's own post via each platform's compose
 * intent (X / Facebook / LINE / WhatsApp), with common + store hashtags,
 * plus a Web Share image button when the store curated a share image.
 * Kept because a repost alone leaves nothing "of their own" on the
 * customer's timeline. Each tap records into `share_logs` for KPI rollup.
 */
export default function ShareButtonsRow({
  storeId,
  storeName,
  snsMedia,
  source,
  onShared,
}: Props) {
  const { t } = useI18n()

  const [showIgHint, setShowIgHint] = useState(false)

  const { xPostUrl, instagramPostUrl } = resolveOfficialPostUrls(snsMedia)
  const repostAvailable = extractTweetId(xPostUrl) !== null
  const igStoryAvailable = isInstagramPostUrl(instagramPostUrl)
  const hasOfficialSection = repostAvailable || igStoryAvailable

  const target: ShareTarget = {
    // shareText overrides storeName so the curated caption appears in the
    // compose box; falls back to plain store name when unset.
    storeName: snsMedia?.shareText || storeName,
    // The own-post tier always links the canonical store page. Amplifying
    // the official post is Tier 1's job — the old sharePostUrl override
    // made Facebook / LINE / WhatsApp share an X post URL instead.
    storeUrl: buildShareUrl(storeId),
    twitterHandle: snsMedia?.twitterHandle,
    hashtags: combineHashtags(snsMedia?.hashtags),
    imageUrl: snsMedia?.shareImageUrl,
  }

  const handle = async (open: () => ShareResult) => {
    const result = open()
    if (!result.attempted) return
    try {
      await api.submitShareLog(storeId, result.platform, source)
    } catch {
      // Logging failure is non-fatal — the share intent has already opened.
    }
    showToast({ type: 'success', message: t('review.shared') })
    onShared?.(result)
  }

  // Web Share API path for the curated image — the OS share sheet lets the
  // user post the image directly to IG Story / any app without in-app
  // navigation. Shown only when the store curated an image.
  const handleImageShare = async () => {
    const ok = await shareImageViaWebShare(target)
    if (!ok) {
      showToast({ type: 'error', message: t('share.imageShareFailed') })
      return
    }
    try {
      await api.submitShareLog(storeId, 'instagram', source)
    } catch {
      // non-fatal
    }
    showToast({ type: 'success', message: t('review.shared') })
    onShared?.({ platform: 'instagram', attempted: true })
  }

  const handleInstagramStoryConfirm = async () => {
    setShowIgHint(false)
    await handle(() => shareToInstagramPost(instagramPostUrl))
  }

  const composeButtons = (
    <div className="grid w-full grid-cols-2 gap-3">
      <button
        onClick={() => handle(() => shareToX(target))}
        className="tap-effect flex h-14 items-center justify-center gap-2 rounded-xl bg-black font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
      >
        {t('review.shareX')}
      </button>
      <button
        onClick={() => handle(() => shareToFacebook(target))}
        className="tap-effect flex h-14 items-center justify-center gap-2 rounded-xl bg-[#1877F2] font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
      >
        {t('review.shareFacebook')}
      </button>
      <button
        onClick={() => handle(() => shareToLine(target))}
        className="tap-effect flex h-14 items-center justify-center gap-2 rounded-xl bg-[#06C755] font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
      >
        {t('review.shareLine')}
      </button>
      <button
        onClick={() => handle(() => shareToWhatsApp(target))}
        className="tap-effect flex h-14 items-center justify-center gap-2 rounded-xl bg-[#25D366] font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
      >
        {t('review.shareWhatsApp')}
      </button>
      {!!snsMedia?.shareImageUrl && (
        <button
          onClick={handleImageShare}
          className="tap-effect col-span-2 flex h-14 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          {t('review.shareInstagram')}
        </button>
      )}
    </div>
  )

  // No official post curated → single-tier layout, visually identical to the
  // pre-#69 UI (no section headings).
  if (!hasOfficialSection) {
    return (
      <div className="w-full">
        {composeButtons}
      </div>
    )
  }

  return (
    <div className="w-full space-y-5">
      {/* Tier 1: amplify the store's official post */}
      <div>
        <h4 className="font-display text-sm font-semibold text-text-primary">
          {t('share.officialSectionTitle')}
        </h4>
        <p className="mt-0.5 font-body text-xs text-text-tertiary">
          {t('share.officialSectionHint')}
        </p>
        <div className="mt-3 flex flex-col gap-3">
          {repostAvailable && (
            <button
              onClick={() => handle(() => shareRepostOnX(xPostUrl))}
              className="tap-effect flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-black font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Repeat2 size={18} />
              {t('share.repostOnX')}
            </button>
          )}
          {igStoryAvailable && (
            <button
              onClick={() => setShowIgHint(true)}
              className="tap-effect flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 font-display text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              {t('share.addToInstagramStory')}
            </button>
          )}
        </div>
      </div>

      {/* Tier 2: the customer's own post */}
      <div>
        <h4 className="mb-3 font-display text-sm font-semibold text-text-primary">
          {t('share.ownPostSectionTitle')}
        </h4>
        {composeButtons}
      </div>

      <InstagramStoryHintModal
        open={showIgHint}
        onClose={() => setShowIgHint(false)}
        onConfirm={handleInstagramStoryConfirm}
      />
    </div>
  )
}
